import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { PostComment } from "../store/actions/authActions/PostComment";
import { InputField } from "./InputField";
import { SolidButton } from "./buttons/SolidButton";

interface FormValues {
  email: string;
  firstName: string;
  lastName: string;
  comment: string;
}

interface Props {
  onCompleted: any;
  setShowCommentModal: React.Dispatch<React.SetStateAction<boolean>>;
  onshowResponseComponent: () => void;
}

export const CommentForm: React.FC<Props> = ({
  onCompleted,
  setShowCommentModal,
  onshowResponseComponent,
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const schema = yup.object().shape({
    email: yup
      .string()
      .email(t("form.validEmail"))
      .max(100, t("form.emailTooLong"))
      .required(t("form.validEmail")),
    firstName: yup
      .string()
      .required(t("form.enterFirstName"))
      .max(100, t("form.firstNameTooLong"))
      .matches(
        /^(?:(?![\]^\\×÷])[A-zÀ-ÿ .'-])+$/i,
        t("error.invalidCharFirstName")
      ),
    lastName: yup
      .string()
      .required(t("form.enterLastName"))
      .max(100, t("form.lastNameTooLong"))
      .matches(
        /^(?:(?![\]^\\×÷])[A-zÀ-ÿ .'-])+$/i,
        t("error.invalidCharLastName")
      ),
    comment: yup
      .string()
      .required(t("form.validComment"))
      .max(500, t("form.commentTooLong"))
      .matches(/^[^<>]*$/, t("error.invalidChar")),
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
  });

  const watchCommentValue = watch("comment");

  const userInfo = useAppSelector((state) => state.auth.userInfo);
  const anonymousMode = userInfo?.anonymousMode || false;
  console.log("this is the userInfo", userInfo);

  return (
    <div className="flex flex-col w-full pt-4 pl-1">
      <button
        onClick={() => {
          onshowResponseComponent();
        }}
        type="button"
        className="text-white flex justify-start pb-2 w-10 hover:opacity-70 transition duration-200 ease-in-out"
      >
        {t("form.back")}
      </button>
      <div className="text-sm">
        <span className="text-slate-300">
          {t("form.contactSupport")}{" "}
          <a
            className="text-blue-500 underline hover:text-blue-600"
            href="mailto: support@liifer.com"
            draggable={false}
          >
            support@liifer.com
          </a>
        </span>
      </div>
      <form
        className="pt-2 w-full"
        onSubmit={handleSubmit((data) => {
          const { firstName, lastName, email, comment } = data;
          dispatch(
            PostComment(
              firstName,
              lastName,
              email,
              comment,
              setLoading,
              onCompleted
            )
          );
        })}
      >
        <div className="space-y-2 py-1">
          <div
            className={` ${
              anonymousMode === true ? "hidden" : "sm:flex"
            } justify-between w-full space-y-2 sm:space-y-0 sm:space-x-2 2xl:space-x-4`}
          >
            <div>
              <InputField
                dark
                register={register("firstName")}
                id="firstName"
                label={t("form.commentFirstName")}
                placeholder={t("form.firstNamePlaceHolder")}
                defaultValue={userInfo ? userInfo.firstName : ""}
                readOnly={!!userInfo}
              />
              <small className="text-red-500">
                {errors.firstName?.message}
              </small>
            </div>
            <div>
              <InputField
                dark
                register={register("lastName")}
                id="lastName"
                label={t("form.commentLastName")}
                placeholder={t("form.lastNamePlaceHolder")}
                defaultValue={userInfo ? userInfo.lastName : ""}
                readOnly={!!userInfo}
              />
              <small className="text-red-500">{errors.lastName?.message}</small>
            </div>
          </div>
          <div className={` ${anonymousMode === true ? "hidden" : "block"}`}>
            <InputField
              dark
              requiredFlag
              register={register("email")}
              id="email"
              inputType="email"
              label={t("form.email")}
              placeholder={t("form.emailPlaceHolder")}
              defaultValue={userInfo ? userInfo.email : ""}
              readOnly={!!userInfo}
            />
            <small className="text-red-500">{errors.email?.message}</small>
          </div>
          <div>
            <InputField
              dark
              requiredFlag
              register={register("comment")}
              id="comment"
              label={t("form.comment")}
              placeholder={t("form.commentPlaceHolder")}
              multiLine
              multiLineMinHeight={100}
              maxLength={500}
              currentValueLength={watchCommentValue?.length | 0}
            />
            <small className="text-red-500">{errors.comment?.message}</small>
          </div>
        </div>
        <div className="flex flex-col justify-end pt-2">
          <div className="pt-2 space-y-2">
            <SolidButton
              backgroundColor="bg-blue-600"
              hoverColor="hover:bg-blue-700"
              squared
              loading={loading}
              width="w-full"
              type="submit"
            >
              <span className="flex items-center text-slate-100">
                {t("form.submit")}
              </span>
            </SolidButton>
          </div>
        </div>
      </form>
    </div>
  );
};
